// Per-option tooltips for Configure chip-multi / select questions, surfaced as the
// little info icon beside each tag (chip-multi) or dropdown option (select). Kept out
// of sections.js to keep the schema readable. QuestionField merges these in when an
// option has no inline tooltip. Strings are English-as-key (wrapped in t()).

export const OPTION_TOOLTIPS = {
  sales_channels: {
    website:
      "You sell through your own website checkout, the most common card-not-present channel.",
    online_store:
      "A hosted storefront (Shopify, WooCommerce) where customers browse and buy your products.",
    web_app: "Customers buy and use your product inside a browser-based application or dashboard.",
    mobile_app:
      "Purchases happen inside your own iOS or Android app, often tied to a logged-in account.",
    app_marketplace:
      "Sales run through Apple App Store or Google Play, where the platform may handle disputes.",
    online_marketplace:
      "You list on third-party marketplaces like Amazon or Etsy that process the payment.",
    social_commerce:
      "Selling via social platforms or live shopping streams such as Instagram, TikTok, or Facebook.",
    online_booking:
      "Customers reserve or book appointments and services online, useful evidence for service disputes.",
    in_person:
      "Card-present sales at a physical point of sale, where chip or PIN data strengthens disputes.",
    moto: "Orders taken by mail or phone where the card is keyed in manually without the cardholder present.",
    sales_team: "Deals closed directly by your sales reps rather than a self-serve checkout.",
    reseller: "Sales made through resellers or partners who sell your product on your behalf."
  },
  fulfillment_verification: {
    tracking_required:
      "A tracking number is recorded for every shipment, the baseline proof for item-not-received disputes.",
    delivery_scan:
      "The carrier's scan confirms the parcel reached its destination, strong evidence against not-received claims.",
    signature_required:
      "A recipient signature on delivery, the strongest proof the order was received.",
    proof_of_delivery:
      "A delivery photo or proof document showing the package at the address, useful for not-received disputes.",
    auto_on_ship:
      "Orders are marked fulfilled at dispatch, so fulfillment reflects shipment rather than confirmed delivery.",
    account_login:
      "Verified by the customer logging into their account, showing they accessed what they bought.",
    access_activated:
      "A license or access is activated for the buyer, proving the digital purchase was made available.",
    digital_download:
      "The file was downloaded by the customer, evidence the digital goods were delivered.",
    credentials_emailed:
      "Login details were emailed to the buyer, showing access to the purchase was provided.",
    usage_logged:
      "Recorded product usage or activity proves the customer actually used what they paid for.",
    service_completion:
      "A sign-off confirms the service was completed, key evidence for service-not-rendered disputes.",
    appointment_attendance:
      "An attendance record confirms the customer showed up for the booked appointment.",
    reservation_checkin:
      "A check-in record shows the customer used their reservation, useful for travel and booking disputes.",
    work_delivered:
      "The deliverable was sent and accepted by the client, proving the work was provided.",
    customer_acceptance:
      "The customer signed off accepting delivery, direct proof they received what was promised."
  },
  checkout_displays: {
    item_name: "The product name appears at checkout so the buyer sees exactly what they ordered.",
    item_description:
      "Product details shown at checkout; helps rebut claims that goods were not as described.",
    quantity: "The number of units ordered is displayed before the customer pays.",
    subtotal: "The pre-tax, pre-shipping price of items is shown before payment.",
    shipping_cost:
      "The delivery charge is itemized at checkout so the buyer sees it before paying.",
    tax: "Applicable tax is shown as a separate line before the customer pays.",
    total:
      "The final amount charged is displayed at checkout, matching the amount on the statement.",
    shipping_address:
      "The delivery address is confirmed at checkout, supporting proof of delivery in disputes.",
    billing_address:
      "The cardholder's billing address is shown, supporting an AVS match against the card.",
    tos_link:
      "A link to your terms is visible at checkout, evidencing the buyer could review them.",
    click_to_accept:
      "The buyer must actively check a box accepting terms; strong proof they agreed to your policies.",
    refund_link:
      "Your refund policy is linked at checkout, countering claims the customer was not informed.",
    subscription_terms:
      "Subscription details are disclosed at checkout, helping defend recurring-billing disputes.",
    recurring_billing_disclosure:
      "The repeat charge and its schedule are stated before payment, key evidence for subscription chargebacks.",
    cancellation_link:
      "Your cancellation policy is linked at checkout, showing the customer how to stop charges.",
    support_contact:
      "Support contact details are shown, evidence the buyer could reach you before disputing."
  },
  tos_acceptance_points: {
    at_signup: "Customer agrees to your terms when first creating their account.",
    at_checkout:
      "Terms are accepted during checkout, tying consent directly to the disputed order.",
    before_payment:
      "Terms are accepted before card details are entered, showing consent preceded the charge.",
    at_renewal:
      "Customer re-confirms terms at each renewal; strong evidence against recurring-billing disputes.",
    at_purchase:
      "Terms are accepted on every order, proving fresh consent for the exact disputed transaction."
  },
  renewal_consent_method: {
    implicit:
      "Renewal proceeds automatically when the customer keeps using the service, with no separate consent step.",
    email_reminder:
      "A reminder email precedes each renewal, but the customer takes no action to confirm it.",
    click_to_renew:
      "The customer actively clicks to approve each renewal, the strongest proof against unrecognized-charge disputes."
  },
  post_purchase_receipts: {
    email:
      "Receipts sent to the buyer's email; emailed confirmations are strong proof the customer knew of the charge.",
    sms: "Receipts texted to the buyer's phone; a delivered SMS confirms the customer was notified of the purchase.",
    customer_portal:
      "Receipts viewable in the customer's online account, showing they could access purchase and billing records.",
    mobile_app:
      "Receipts shown inside your mobile app, evidencing the customer's access to order details.",
    in_person:
      "A printed or handed receipt given at the point of sale during a face-to-face purchase."
  },
  policy_display_at_checkout: {
    product_page: "Policies shown on the item's page, where the customer decides to buy.",
    pricing_page:
      "Policies shown alongside prices and plans, useful for proving subscription terms were visible.",
    footer_link: "A site-wide footer link to your policies, accessible from every page.",
    terms_page: "A dedicated Terms and Conditions page holding your full policy text.",
    checkout_page:
      "Policies appear on the payment screen, proving disclosure right before the customer paid.",
    click_to_accept:
      "Buyer ticks a box to accept terms at checkout, the strongest proof of agreement.",
    email_confirmation:
      "Policies included in the order confirmation email, a timestamped record sent to the buyer.",
    customer_portal:
      "Policies available in the logged-in account area customers can revisit anytime.",
    sms: "Policy links or text sent by SMS, giving a timestamped delivery record.",
    mobile_app: "Policies shown inside your mobile app during or after purchase.",
    physical_copy: "A printed copy handed over in person, used for face-to-face sales."
  },
  account_evidence_records: {
    account_creation_date:
      "When the customer first registered; a long-standing account undercuts claims the purchase was fraudulent.",
    login_history:
      "Timestamps of past sign-ins showing the genuine account holder, not a fraudster, accessed the account.",
    device_information:
      "Devices used to access the account; matching the order device ties the cardholder to the purchase.",
    ip_address:
      "IP addresses across sessions; a consistent location links the real customer to the disputed order.",
    order_history:
      "Prior purchases on the account; a repeat-buyer pattern strongly rebuts fraud and not-recognized claims.",
    password_reset_history:
      "Records of password changes confirming the account holder controlled and maintained the account.",
    saved_payment_method:
      "History of cards stored on file, showing the cardholder added and reused the disputed payment method."
  },
  subscription_signup_method: {
    single_click:
      "The customer starts the subscription in one click, with no extra confirmation step.",
    double_optin:
      "The customer confirms a second time (often by email) before the subscription begins, proving deliberate intent.",
    explicit_consent_modal:
      "A pop-up shows the recurring terms and requires the customer to actively agree, strong proof against unauthorized-subscription disputes."
  },
  subscription_consent: {
    implicit:
      'Recurring charges continue without a fresh confirmation; weakest position against "unauthorized subscription" disputes.',
    explicit_at_signup:
      "The customer expressly agreed to recurring billing when subscribing, proving authorization for later charges.",
    explicit_at_renewal:
      "The customer re-confirms before each renewal, the strongest proof every recurring charge was authorized."
  },
  industry: {
    ecommerce:
      "You sell physical goods online; disputes usually turn on shipping and delivery proof.",
    saas: "Cloud software sold by subscription; login and usage records become key dispute evidence.",
    subscription:
      "Recurring billing for products or services; consent and renewal disclosures drive these disputes.",
    marketplace:
      "You connect third-party buyers and sellers; evidence often spans both sides of a transaction.",
    digital_goods:
      "You sell downloads, media, or licenses; delivery and access logs prove the purchase.",
    services:
      "You provide work or labor rather than products; completion sign-off is the core evidence.",
    travel:
      "You sell flights, lodging, or bookings; itineraries and check-in records support disputes.",
    gaming: "You sell games or in-game items; account and play history prove the customer used it.",
    financial:
      "You offer financial products or services, which face stricter rules and identity-verification scrutiny.",
    crypto:
      "You deal in digital currencies or assets; settlement and wallet records support disputes.",
    other: "Your business does not fit the listed categories; pick this if none apply."
  },
  signup_methods: {
    none: "No identity checks run when a customer creates an account.",
    email_verification: "Customer confirms a link or code sent to their email address.",
    phone_verification: "Customer confirms a code sent to their phone by SMS or call.",
    gov_id: "Customer uploads a government-issued ID to verify their identity.",
    biometric: "A face or fingerprint check confirms the customer is a real person.",
    bank_statement: "Customer submits a bank statement to confirm their identity or address.",
    proof_of_address: "Customer provides a document confirming their residential address."
  }
}

export default OPTION_TOOLTIPS
